import React, { useState, useEffect } from 'react';
import { transactionApprovalsAPI, categoriesAPI, banksAPI, transactionsAPI } from '../services/api';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Alert,
  Box,
  Tabs,
  Tab,
  Grid,
  IconButton,
  CircularProgress,
  List,
  ListItem,
  Paper,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Warning,
  Settings,
  Refresh,
  Add,
  Delete,
  CheckBox,
  CheckBoxOutlineBlank,
  Help,
  Search,
  Edit
} from '@mui/icons-material';

interface Transaction {
  id: number;
  amount: number;
  description: string;
  date: string;
  transaction_type: 'income' | 'expense' | 'transfer';
  category: { id: number; name: string; color: string };
  bank: { id: number; name: string };
  approved: boolean;
  auto_approved: boolean;
  duplicate_flag: boolean;
  approval_notes?: string;
}

interface AutoApprovalRule {
  id: number;
  keyword: string;
  category: { id: number; name: string; color: string };
  auto_approve: boolean;
}

interface Category {
  id: number;
  name: string;
  color: string;
}

interface Bank {
  id: number;
  name: string;
  code: string;
}

const TransactionApprovalsPage: React.FC = () => {
  const [pendingTransactions, setPendingTransactions] = useState<Transaction[]>([]);
  const [duplicateTransactions, setDuplicateTransactions] = useState<Transaction[]>([]);
  const [unknownTransactions, setUnknownTransactions] = useState<Transaction[]>([]);
  const [autoApprovalRules, setAutoApprovalRules] = useState<AutoApprovalRule[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [banks, setBanks] = useState<Bank[]>([]);
  const [selectedTransactions, setSelectedTransactions] = useState<number[]>([]);
  const [bulkNotes, setBulkNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('pending');

  // Auto-approval rule form
  const [newRuleKeyword, setNewRuleKeyword] = useState('');
  const [newRuleCategoryId, setNewRuleCategoryId] = useState<number | ''>('');

  // Unknown transactions form
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | ''>('');
  const [categoryKeywords, setCategoryKeywords] = useState('');

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'income' | 'expense' | 'transfer'>('all');
  const [filterCategory, setFilterCategory] = useState<number | ''>('');
  const [filterBank, setFilterBank] = useState<number | ''>('');

  // Edit category states
  const [editingTransactionId, setEditingTransactionId] = useState<number | null>(null);
  const [editingCategoryId, setEditingCategoryId] = useState<number | ''>('');

  // Auto-approval rule edit states
  const [editingRuleId, setEditingRuleId] = useState<number | null>(null);
  const [editingRuleKeyword, setEditingRuleKeyword] = useState('');
  const [editingRuleCategoryId, setEditingRuleCategoryId] = useState<number | ''>('');

  // Edit transaction dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState<Transaction | null>(null);
  const [editFormData, setEditFormData] = useState({
    amount: '',
    description: '',
    date: '',
    transaction_type: 'expense' as 'income' | 'expense' | 'transfer',
    category_id: '',
    bank_id: '',
    to_bank_id: '',
    to_bank_name: '',
    reference_number: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  // Clear selections when filters change
  useEffect(() => {
    setSelectedTransactions([]);
  }, [searchTerm, filterType, filterCategory, filterBank]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [approvalsResponse, categoriesResponse, banksResponse] = await Promise.all([
        transactionApprovalsAPI.getApprovals(),
        categoriesAPI.getAll(),
        banksAPI.getAll()
      ]);

      setPendingTransactions(approvalsResponse.data.pending_transactions || []);
      setDuplicateTransactions(approvalsResponse.data.duplicate_transactions || []);
      setUnknownTransactions(approvalsResponse.data.unknown_transactions || []);
      setAutoApprovalRules(approvalsResponse.data.auto_approval_rules || []);
      // Handle new API response format for categories
      setCategories(categoriesResponse.data.data || categoriesResponse.data || []);
      // Handle new API response format for banks
      setBanks(banksResponse.data.data || banksResponse.data || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load approval data');
    } finally {
      setLoading(false);
    }
  };

  // Filter functions
  const filterTransactions = (transactions: Transaction[]) => {
    return transactions.filter(transaction => {
      // Search term filter
      if (searchTerm && !transaction.description.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }
      
      // Transaction type filter
      if (filterType !== 'all' && transaction.transaction_type !== filterType) {
        return false;
      }
      
      // Category filter
      if (filterCategory && transaction.category.id !== filterCategory) {
        return false;
      }
      
      // Bank filter
      if (filterBank && transaction.bank.id !== filterBank) {
        return false;
      }
      
      return true;
    });
  };

  const getFilteredTransactions = () => {
    switch (activeTab) {
      case 'pending':
        return filterTransactions(pendingTransactions);
      case 'unknown':
        return filterTransactions(unknownTransactions);
      case 'duplicates':
        return filterTransactions(duplicateTransactions);
      default:
        return [];
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterType('all');
    setFilterCategory('');
    setFilterBank('');
    setSelectedTransactions([]); // Clear selections when filters change
  };

  // Edit category functions
  const handleEditCategory = (transactionId: number, currentCategoryId: number) => {
    setEditingTransactionId(transactionId);
    setEditingCategoryId(currentCategoryId);
  };

  const handleSaveCategory = async (transactionId: number) => {
    if (!editingCategoryId) return;

    try {
      await transactionApprovalsAPI.updateTransactionCategory(transactionId, Number(editingCategoryId));
      setSuccessMessage('Category updated successfully');
      setEditingTransactionId(null);
      setEditingCategoryId('');
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update category');
    }
  };

  const handleCancelEdit = () => {
    setEditingTransactionId(null);
    setEditingCategoryId('');
  };

  // Auto-approval rule edit functions
  const handleEditRule = (rule: AutoApprovalRule) => {
    setEditingRuleId(rule.id);
    setEditingRuleKeyword(rule.keyword);
    setEditingRuleCategoryId(rule.category.id);
  };

  const handleSaveRule = async () => {
    if (!editingRuleKeyword || !editingRuleCategoryId) return;

    try {
      await transactionApprovalsAPI.updateAutoApprovalRule(
        editingRuleId!,
        editingRuleKeyword,
        Number(editingRuleCategoryId)
      );
      setSuccessMessage('Auto-approval rule updated successfully');
      setEditingRuleId(null);
      setEditingRuleKeyword('');
      setEditingRuleCategoryId('');
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update auto-approval rule');
    }
  };

  const handleDeleteRule = async (ruleId: number) => {
    if (!window.confirm('Are you sure you want to delete this auto-approval rule?')) {
      return;
    }

    try {
      await transactionApprovalsAPI.deleteAutoApprovalRule(ruleId);
      setSuccessMessage('Auto-approval rule deleted successfully');
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete auto-approval rule');
    }
  };

  const handleCancelEditRule = () => {
    setEditingRuleId(null);
    setEditingRuleKeyword('');
    setEditingRuleCategoryId('');
  };

  // Edit transaction functions
  const handleEditTransaction = (transaction: Transaction) => {
    setEditingTransaction(transaction);
    
    // Format date for datetime-local input
    const formatDateForInput = (dateString: string) => {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    const isTransfer = !!(transaction as any).to_bank || !!(transaction as any).to_bank_name;
    setEditFormData({
      amount: transaction.amount.toString(),
      description: transaction.description,
      date: formatDateForInput(transaction.date),
      transaction_type: isTransfer ? 'transfer' : transaction.transaction_type,
      category_id: transaction.category.id.toString(),
      bank_id: transaction.bank.id.toString(),
      to_bank_id: (transaction as any).to_bank?.id?.toString() || '',
      to_bank_name: (transaction as any).to_bank_name || '',
      reference_number: (transaction as any).reference_number || ''
    });
    
    setEditDialogOpen(true);
  };

  const handleSaveTransaction = async () => {
    if (!editingTransaction) return;

    // Backend accepts only 'income' or 'expense', not 'transfer'
    const txType = editFormData.transaction_type === 'transfer'
      ? (editingTransaction as any).transaction_type
      : editFormData.transaction_type;

    try {
      await transactionsAPI.update(editingTransaction.id, {
        amount: parseFloat(editFormData.amount),
        description: editFormData.description,
        date: editFormData.date,
        transaction_type: txType as 'income' | 'expense',
        category_id: parseInt(editFormData.category_id),
        bank_id: parseInt(editFormData.bank_id),
        to_bank_id: editFormData.to_bank_id ? parseInt(editFormData.to_bank_id) : undefined,
        to_bank_name: editFormData.to_bank_name || undefined,
        reference_number: editFormData.reference_number || undefined
      });
      
      setSuccessMessage('Transaction updated successfully');
      setEditDialogOpen(false);
      setEditingTransaction(null);
      loadData();
    } catch (err: any) {
      const msg = err.response?.data?.errors?.join(', ')
        || err.response?.data?.message
        || err.response?.data?.error
        || 'Failed to update transaction';
      setError(msg);
    }
  };

  const handleCancelEditTransaction = () => {
    setEditDialogOpen(false);
    setEditingTransaction(null);
  };

  const handleApprove = async (transactionId: number, notes?: string) => {
    try {
      await transactionApprovalsAPI.approve(transactionId, notes);
      setSuccessMessage('Transaction approved successfully');
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to approve transaction');
    }
  };

  const handleReject = async (transactionId: number, notes?: string) => {
    try {
      await transactionApprovalsAPI.reject(transactionId, notes);
      setSuccessMessage('Transaction rejected');
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reject transaction');
    }
  };

  const handleMarkDuplicate = async (transactionId: number) => {
    try {
      await transactionApprovalsAPI.markDuplicate(transactionId);
      setSuccessMessage('Transaction marked as duplicate');
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to mark as duplicate');
    }
  };

  const handleDeleteTransaction = async (transactionId: number) => {
    if (window.confirm('Are you sure you want to delete this transaction? This action cannot be undone.')) {
      try {
        await transactionApprovalsAPI.deleteTransaction(transactionId);
        setSuccessMessage('Transaction deleted successfully');
        loadData();
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to delete transaction');
      }
    }
  };

  const handleBulkDelete = async () => {
    if (selectedTransactions.length === 0) return;
    
    if (window.confirm(`Are you sure you want to delete ${selectedTransactions.length} transactions? This action cannot be undone.`)) {
      try {
        await transactionApprovalsAPI.bulkDelete(selectedTransactions);
        setSuccessMessage(`${selectedTransactions.length} transactions deleted successfully`);
        setSelectedTransactions([]);
        loadData();
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to delete transactions');
      }
    }
  };

  const handleBulkApprove = async () => {
    if (selectedTransactions.length === 0) return;
    
    try {
      await transactionApprovalsAPI.bulkApprove(selectedTransactions, bulkNotes);
      setSuccessMessage(`${selectedTransactions.length} transactions approved successfully`);
      setSelectedTransactions([]);
      setBulkNotes('');
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to bulk approve transactions');
    }
  };

  const handleBulkReject = async () => {
    if (selectedTransactions.length === 0) return;
    
    try {
      await transactionApprovalsAPI.bulkReject(selectedTransactions, bulkNotes);
      setSuccessMessage(`${selectedTransactions.length} transactions rejected`);
      setSelectedTransactions([]);
      setBulkNotes('');
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to bulk reject transactions');
    }
  };

  const handleCreateAutoApprovalRule = async () => {
    if (!newRuleKeyword || !newRuleCategoryId) return;
    
    try {
      const response = await transactionApprovalsAPI.createAutoApprovalRule(newRuleKeyword, Number(newRuleCategoryId));
      const updated = response.data?.updated_count ?? 0;
      setSuccessMessage(
        updated > 0
          ? `Auto-approval rule created. Applied to ${updated} existing unknown transaction(s).`
          : 'Auto-approval rule created successfully'
      );
      setNewRuleKeyword('');
      setNewRuleCategoryId('');
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create auto-approval rule');
    }
  };

  const handleApplyRulesToUnknown = async () => {
    try {
      const response = await transactionApprovalsAPI.applyRulesToUnknown();
      const count = response.data?.updated_count ?? 0;
      setSuccessMessage(
        count > 0
          ? `Applied auto-approval rules to ${count} transaction(s).`
          : 'No unknown transactions matched existing rules.'
      );
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to apply rules to unknown transactions');
    }
  };

  const handleAssignCategoryToUnknown = async () => {
    if (!selectedCategoryId || selectedTransactions.length === 0) return;
    
    try {
      const keywords = categoryKeywords ? categoryKeywords.split(',').map(k => k.trim()) : [];
      await transactionApprovalsAPI.assignCategoryToUnknown(
        selectedTransactions, 
        Number(selectedCategoryId), 
        keywords
      );
      setSuccessMessage(`${selectedTransactions.length} transactions categorized and approved`);
      setSelectedTransactions([]);
      setSelectedCategoryId('');
      setCategoryKeywords('');
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to assign category');
    }
  };

  const handleSelectTransaction = (transactionId: number, checked: boolean) => {
    if (checked) {
      setSelectedTransactions([...selectedTransactions, transactionId]);
    } else {
      setSelectedTransactions(selectedTransactions.filter(id => id !== transactionId));
    }
  };

  const handleSelectAll = (checked: boolean, transactions: Transaction[]) => {
    if (checked) {
      setSelectedTransactions(transactions.map(t => t.id));
    } else {
      setSelectedTransactions([]);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading approval data...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Approvals
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={loadData}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Pending Approval
                  </Typography>
                  <Typography variant="h4" component="div">
                    {pendingTransactions.length}
                  </Typography>
                </Box>
                <Warning color="warning" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Unknown Transactions
                  </Typography>
                  <Typography variant="h4" component="div">
                    {unknownTransactions.length}
                  </Typography>
                </Box>
                <Help color="info" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Duplicates
                  </Typography>
                  <Typography variant="h4" component="div">
                    {duplicateTransactions.length}
                  </Typography>
                </Box>
                <Cancel color="error" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Auto-Approval Rules
                  </Typography>
                  <Typography variant="h4" component="div">
                    {autoApprovalRules.length}
                  </Typography>
                </Box>
                <Settings color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ width: '100%' }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab 
            label={`Pending Approval (${pendingTransactions.length})`} 
            value="pending" 
          />
          <Tab 
            label={`Unknown Transactions (${unknownTransactions.length})`} 
            value="unknown" 
          />
          <Tab 
            label={`Duplicates (${duplicateTransactions.length})`} 
            value="duplicates" 
          />
          <Tab 
            label={`Auto-Approval Rules (${autoApprovalRules.length})`} 
            value="rules" 
          />
        </Tabs>

        {/* Search and Filter Section */}
        {activeTab !== 'rules' && (
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', backgroundColor: '#f5f5f5' }}>
            <Grid container spacing={2} alignItems="center">
              {/* Search Bar */}
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  placeholder="Search by description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  size="small"
                  InputProps={{
                    startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
              </Grid>

              {/* Transaction Type Filter */}
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value as any)}
                    label="Type"
                  >
                    <MenuItem value="all">All Types</MenuItem>
                    <MenuItem value="income">Income</MenuItem>
                    <MenuItem value="expense">Expense</MenuItem>
                    <MenuItem value="transfer">Transfer</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Category Filter */}
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={filterCategory.toString()}
                    onChange={(e) => setFilterCategory(Number(e.target.value) || '')}
                    label="Category"
                  >
                    <MenuItem value="">All Categories</MenuItem>
                    {categories.map((category) => (
                      <MenuItem key={category.id} value={category.id.toString()}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              backgroundColor: category.color
                            }}
                          />
                          <span>{category.name}</span>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Bank Filter */}
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Bank</InputLabel>
                  <Select
                    value={filterBank.toString()}
                    onChange={(e) => setFilterBank(Number(e.target.value) || '')}
                    label="Bank"
                  >
                    <MenuItem value="">All Banks</MenuItem>
                    {Array.from(new Set([...pendingTransactions, ...unknownTransactions, ...duplicateTransactions]
                      .map(t => t.bank.id))).map((bankId) => {
                      const bank = [...pendingTransactions, ...unknownTransactions, ...duplicateTransactions]
                        .find(t => t.bank.id === bankId)?.bank;
                      return bank ? (
                        <MenuItem key={bankId} value={bankId.toString()}>
                          {bank.name}
                        </MenuItem>
                      ) : null;
                    })}
                  </Select>
                </FormControl>
              </Grid>

              {/* Clear Filters Button */}
              <Grid item xs={12} md={2}>
                <Button
                  variant="outlined"
                  onClick={clearFilters}
                  size="small"
                  fullWidth
                >
                  Clear Filters
                </Button>
              </Grid>
            </Grid>

            {/* Results Summary */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="textSecondary">
                Showing {getFilteredTransactions().length} of {
                  activeTab === 'pending' ? pendingTransactions.length :
                  activeTab === 'unknown' ? unknownTransactions.length :
                  duplicateTransactions.length
                } transactions
              </Typography>
            </Box>
          </Box>
        )}

        <Box sx={{ p: 3 }}>
          {activeTab === 'pending' && (
            <Box>
              {getFilteredTransactions().length > 0 ? (
                <Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Box display="flex" alignItems="center" gap={2}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedTransactions.length === getFilteredTransactions().length}
                            onChange={(e) => handleSelectAll(e.target.checked, getFilteredTransactions())}
                            icon={<CheckBoxOutlineBlank />}
                            checkedIcon={<CheckBox />}
                          />
                        }
                        label={`${selectedTransactions.length} of ${getFilteredTransactions().length} selected`}
                      />
                    </Box>
                    
                    {selectedTransactions.length > 0 && (
                      <Box display="flex" alignItems="center" gap={2}>
                        <TextField
                          placeholder="Bulk action notes (optional)"
                          value={bulkNotes}
                          onChange={(e) => setBulkNotes(e.target.value)}
                          size="small"
                          sx={{ width: 300 }}
                        />
                        <Button
                          variant="contained"
                          color="success"
                          startIcon={<CheckCircle />}
                          onClick={handleBulkApprove}
                          size="small"
                        >
                          Approve Selected
                        </Button>
                        <Button
                          variant="contained"
                          color="error"
                          startIcon={<Cancel />}
                          onClick={handleBulkReject}
                          size="small"
                        >
                          Reject Selected
                        </Button>
                        <Button
                          variant="contained"
                          color="error"
                          startIcon={<Delete />}
                          onClick={handleBulkDelete}
                          size="small"
                        >
                          Delete Selected
                        </Button>
                      </Box>
                    )}
                  </Box>

                  <List>
                    {getFilteredTransactions().map((transaction) => (
                      <ListItem key={transaction.id} divider>
                        <Box display="flex" alignItems="center" gap={2} sx={{ width: '100%' }}>
                          <Checkbox
                            checked={selectedTransactions.includes(transaction.id)}
                            onChange={(e) => handleSelectTransaction(transaction.id, e.target.checked)}
                          />
                          <Box sx={{ flexGrow: 1 }}>
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                              <Typography variant="subtitle1" fontWeight="bold">
                                {transaction.description}
                              </Typography>
                              <Chip
                                label={transaction.transaction_type}
                                color={transaction.transaction_type === 'expense' ? 'error' : 'success'}
                                size="small"
                              />
                              {transaction.auto_approved && (
                                <Chip label="Auto-Approved" variant="outlined" size="small" />
                              )}
                            </Box>
                            <Box display="flex" alignItems="center" gap={2}>
                              <Typography variant="body2" color="textSecondary">
                                {formatCurrency(transaction.amount)}
                              </Typography>
                              <Typography variant="body2" color="textSecondary">
                                {formatDate(transaction.date)}
                              </Typography>
                              <Typography variant="body2" color="textSecondary">
                                {transaction.bank.name}
                              </Typography>
                              {editingTransactionId === transaction.id ? (
                                <Box display="flex" alignItems="center" gap={1}>
                                  <FormControl size="small" sx={{ minWidth: 150 }}>
                                    <Select
                                      value={editingCategoryId.toString()}
                                      onChange={(e) => setEditingCategoryId(Number(e.target.value) || '')}
                                      size="small"
                                    >
                                      {categories.map((category) => (
                                        <MenuItem key={category.id} value={category.id.toString()}>
                                          <Box display="flex" alignItems="center" gap={1}>
                                            <Box
                                              sx={{
                                                width: 12,
                                                height: 12,
                                                borderRadius: '50%',
                                                backgroundColor: category.color
                                              }}
                                            />
                                            <span>{category.name}</span>
                                          </Box>
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                  <IconButton
                                    size="small"
                                    color="success"
                                    onClick={() => handleSaveCategory(transaction.id)}
                                    disabled={!editingCategoryId}
                                  >
                                    <CheckCircle />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={handleCancelEdit}
                                  >
                                    <Cancel />
                                  </IconButton>
                                </Box>
                              ) : (
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Chip
                                    label={transaction.category.name}
                                    size="small"
                                    sx={{ 
                                      backgroundColor: transaction.category.color,
                                      color: 'white'
                                    }}
                                  />
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEditCategory(transaction.id, transaction.category.id)}
                                  >
                                    <Edit />
                                  </IconButton>
                                </Box>
                              )}
                            </Box>
                            {transaction.approval_notes && (
                              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                                Notes: {transaction.approval_notes}
                              </Typography>
                            )}
                          </Box>
                          
                          <Box display="flex" gap={1}>
                            <IconButton
                              size="small"
                              onClick={() => handleEditTransaction(transaction)}
                              color="primary"
                            >
                              <Edit />
                            </IconButton>
                            <Button
                              variant="contained"
                              color="success"
                              startIcon={<CheckCircle />}
                              onClick={() => handleApprove(transaction.id)}
                              size="small"
                            >
                              Approve
                            </Button>
                            <Button
                              variant="contained"
                              color="error"
                              startIcon={<Cancel />}
                              onClick={() => handleReject(transaction.id)}
                              size="small"
                            >
                              Reject
                            </Button>
                            <Button
                              variant="outlined"
                              startIcon={<Warning />}
                              onClick={() => handleMarkDuplicate(transaction.id)}
                              size="small"
                            >
                              Duplicate
                            </Button>
                            <Button
                              variant="outlined"
                              color="error"
                              startIcon={<Delete />}
                              onClick={() => handleDeleteTransaction(transaction.id)}
                              size="small"
                            >
                              Delete
                            </Button>
                          </Box>
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              ) : (
                <Box textAlign="center" py={4}>
                  <CheckCircle color="success" sx={{ fontSize: 60, mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    {searchTerm || filterType !== 'all' || filterCategory || filterBank ? 'No matching transactions' : 'No Pending Approvals'}
                  </Typography>
                  <Typography color="textSecondary">
                    {searchTerm || filterType !== 'all' || filterCategory || filterBank ? 'Try adjusting your search or filters' : 'All transactions have been approved!'}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {activeTab === 'unknown' && (
            <Box>
              {getFilteredTransactions().length > 0 ? (
                <Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Box display="flex" alignItems="center" gap={2}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedTransactions.length === getFilteredTransactions().length}
                            onChange={(e) => handleSelectAll(e.target.checked, getFilteredTransactions())}
                            icon={<CheckBoxOutlineBlank />}
                            checkedIcon={<CheckBox />}
                          />
                        }
                        label={`${selectedTransactions.length} of ${getFilteredTransactions().length} selected`}
                      />
                    </Box>
                    
                    {selectedTransactions.length > 0 && (
                      <Box display="flex" alignItems="center" gap={2}>
                        <FormControl sx={{ minWidth: 200 }}>
                          <InputLabel>Select category</InputLabel>
                          <Select
                            value={selectedCategoryId.toString()}
                            onChange={(e) => setSelectedCategoryId(Number(e.target.value))}
                            label="Select category"
                          >
                            {categories.filter(cat => cat.name !== '❓ Unknown').map((category) => (
                              <MenuItem key={category.id} value={category.id.toString()}>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Box
                                    sx={{
                                      width: 12,
                                      height: 12,
                                      borderRadius: '50%',
                                      backgroundColor: category.color
                                    }}
                                  />
                                  <span>{category.name}</span>
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <TextField
                          placeholder="Keywords (comma-separated, optional)"
                          value={categoryKeywords}
                          onChange={(e) => setCategoryKeywords(e.target.value)}
                          size="small"
                          sx={{ width: 300 }}
                        />
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<Add />}
                          onClick={handleAssignCategoryToUnknown}
                          disabled={!selectedCategoryId}
                          size="small"
                        >
                          Assign Category & Approve
                        </Button>
                        <Button
                          variant="contained"
                          color="error"
                          startIcon={<Delete />}
                          onClick={handleBulkDelete}
                          size="small"
                        >
                          Delete Selected
                        </Button>
                      </Box>
                    )}
                  </Box>

                  <List>
                    {getFilteredTransactions().map((transaction) => (
                      <ListItem key={transaction.id} divider>
                        <Box display="flex" alignItems="center" gap={2} sx={{ width: '100%' }}>
                          <Checkbox
                            checked={selectedTransactions.includes(transaction.id)}
                            onChange={(e) => handleSelectTransaction(transaction.id, e.target.checked)}
                          />
                          <Box sx={{ flexGrow: 1 }}>
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                              <Typography variant="subtitle1" fontWeight="bold">
                                {transaction.description}
                              </Typography>
                              <Chip
                                label={transaction.transaction_type}
                                color={transaction.transaction_type === 'expense' ? 'error' : 'success'}
                                size="small"
                              />
                              <Chip label="Unknown Category" color="warning" size="small" />
                            </Box>
                            <Box display="flex" alignItems="center" gap={2}>
                              <Typography variant="body2" color="textSecondary">
                                {formatCurrency(transaction.amount)}
                              </Typography>
                              <Typography variant="body2" color="textSecondary">
                                {formatDate(transaction.date)}
                              </Typography>
                              <Typography variant="body2" color="textSecondary">
                                {transaction.bank.name}
                              </Typography>
                            </Box>
                          </Box>
                          <Box display="flex" gap={1}>
                            <IconButton
                              size="small"
                              onClick={() => handleEditTransaction(transaction)}
                              color="primary"
                            >
                              <Edit />
                            </IconButton>
                            <Button
                              variant="outlined"
                              color="error"
                              startIcon={<Delete />}
                              onClick={() => handleDeleteTransaction(transaction.id)}
                              size="small"
                            >
                              Delete
                            </Button>
                          </Box>
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              ) : (
                <Box textAlign="center" py={4}>
                  <CheckCircle color="success" sx={{ fontSize: 60, mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    {searchTerm || filterType !== 'all' || filterCategory || filterBank ? 'No matching transactions' : 'No Unknown Transactions'}
                  </Typography>
                  <Typography color="textSecondary">
                    {searchTerm || filterType !== 'all' || filterCategory || filterBank ? 'Try adjusting your search or filters' : 'All transactions have been properly categorized!'}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {activeTab === 'duplicates' && (
            <Box>
              {getFilteredTransactions().length > 0 ? (
                <List>
                  {getFilteredTransactions().map((transaction) => (
                    <ListItem key={transaction.id} divider>
                      <Box sx={{ flexGrow: 1 }}>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {transaction.description}
                          </Typography>
                          <Chip label="Duplicate" color="error" size="small" />
                        </Box>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Typography variant="body2" color="textSecondary">
                            {formatCurrency(transaction.amount)}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {formatDate(transaction.date)}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {transaction.bank.name}
                          </Typography>
                          {editingTransactionId === transaction.id ? (
                            <Box display="flex" alignItems="center" gap={1}>
                              <FormControl size="small" sx={{ minWidth: 150 }}>
                                <Select
                                  value={editingCategoryId.toString()}
                                  onChange={(e) => setEditingCategoryId(Number(e.target.value) || '')}
                                  size="small"
                                >
                                  {categories.map((category) => (
                                    <MenuItem key={category.id} value={category.id.toString()}>
                                      <Box display="flex" alignItems="center" gap={1}>
                                        <Box
                                          sx={{
                                            width: 12,
                                            height: 12,
                                            borderRadius: '50%',
                                            backgroundColor: category.color
                                          }}
                                        />
                                        <span>{category.name}</span>
                                      </Box>
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleSaveCategory(transaction.id)}
                                disabled={!editingCategoryId}
                              >
                                <CheckCircle />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={handleCancelEdit}
                              >
                                <Cancel />
                              </IconButton>
                            </Box>
                          ) : (
                            <Box display="flex" alignItems="center" gap={1}>
                              <Chip
                                label={transaction.category.name}
                                size="small"
                                sx={{ 
                                  backgroundColor: transaction.category.color,
                                  color: 'white'
                                }}
                              />
                              <IconButton
                                size="small"
                                onClick={() => handleEditCategory(transaction.id, transaction.category.id)}
                              >
                                <Edit />
                              </IconButton>
                            </Box>
                          )}
                        </Box>
                        {transaction.approval_notes && (
                          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                            Notes: {transaction.approval_notes}
                          </Typography>
                        )}
                      </Box>
                      
                      <Box display="flex" gap={1}>
                        <IconButton
                          size="small"
                          onClick={() => handleEditTransaction(transaction)}
                          color="primary"
                        >
                          <Edit />
                        </IconButton>
                        <Button
                          variant="contained"
                          color="success"
                          startIcon={<CheckCircle />}
                          onClick={() => handleApprove(transaction.id)}
                          size="small"
                        >
                          Keep
                        </Button>
                        <Button
                          variant="contained"
                          color="error"
                          startIcon={<Delete />}
                          onClick={() => handleDeleteTransaction(transaction.id)}
                          size="small"
                        >
                          Delete
                        </Button>
                      </Box>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Box textAlign="center" py={4}>
                  <CheckCircle color="success" sx={{ fontSize: 60, mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    {searchTerm || filterType !== 'all' || filterCategory || filterBank ? 'No matching transactions' : 'No Duplicates Found'}
                  </Typography>
                  <Typography color="textSecondary">
                    {searchTerm || filterType !== 'all' || filterCategory || filterBank ? 'Try adjusting your search or filters' : 'All transactions are unique!'}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {activeTab === 'rules' && (
            <Box>
              <Card sx={{ mb: 3 }}>
                <CardHeader
                  title="Create Auto-Approval Rule"
                  action={
                    unknownTransactions.length > 0 ? (
                      <Button variant="outlined" size="small" onClick={handleApplyRulesToUnknown}>
                        Apply rules to {unknownTransactions.length} unknown
                      </Button>
                    ) : null
                  }
                />
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2}>
                    <TextField
                      placeholder="Keyword (e.g., 'supermarket')"
                      value={newRuleKeyword}
                      onChange={(e) => setNewRuleKeyword(e.target.value)}
                      sx={{ flexGrow: 1 }}
                    />
                    <FormControl sx={{ minWidth: 200 }}>
                      <InputLabel>Select category</InputLabel>
                      <Select
                        value={newRuleCategoryId.toString()}
                        onChange={(e) => setNewRuleCategoryId(Number(e.target.value))}
                        label="Select category"
                      >
                        {categories.map((category) => (
                          <MenuItem key={category.id} value={category.id.toString()}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Box
                                sx={{
                                  width: 12,
                                  height: 12,
                                  borderRadius: '50%',
                                  backgroundColor: category.color
                                }}
                              />
                              <span>{category.name}</span>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button
                      variant="contained"
                      startIcon={<Add />}
                      onClick={handleCreateAutoApprovalRule}
                      disabled={!newRuleKeyword || !newRuleCategoryId}
                    >
                      Create Rule
                    </Button>
                  </Box>
                </CardContent>
              </Card>

              <List>
                {autoApprovalRules.map((rule) => (
                  <ListItem key={rule.id} divider>
                    <Box display="flex" alignItems="center" gap={2} sx={{ width: '100%' }}>
                      {editingRuleId === rule.id ? (
                        <Box display="flex" alignItems="center" gap={2} sx={{ flexGrow: 1 }}>
                          <TextField
                            placeholder="Keyword"
                            value={editingRuleKeyword}
                            onChange={(e) => setEditingRuleKeyword(e.target.value)}
                            size="small"
                            sx={{ minWidth: 200 }}
                          />
                          <Typography color="textSecondary">→</Typography>
                          <FormControl size="small" sx={{ minWidth: 200 }}>
                            <Select
                              value={editingRuleCategoryId.toString()}
                              onChange={(e) => setEditingRuleCategoryId(Number(e.target.value) || '')}
                              size="small"
                            >
                              {categories.map((category) => (
                                <MenuItem key={category.id} value={category.id.toString()}>
                                  <Box display="flex" alignItems="center" gap={1}>
                                    <Box
                                      sx={{
                                        width: 12,
                                        height: 12,
                                        borderRadius: '50%',
                                        backgroundColor: category.color
                                      }}
                                    />
                                    <span>{category.name}</span>
                                  </Box>
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <IconButton
                            size="small"
                            color="success"
                            onClick={handleSaveRule}
                            disabled={!editingRuleKeyword || !editingRuleCategoryId}
                          >
                            <CheckCircle />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={handleCancelEditRule}
                          >
                            <Cancel />
                          </IconButton>
                        </Box>
                      ) : (
                        <>
                          <Typography variant="body1" fontWeight="bold">
                            "{rule.keyword}"
                          </Typography>
                          <Typography color="textSecondary">→</Typography>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                backgroundColor: rule.category.color
                              }}
                            />
                            <Typography>{rule.category.name}</Typography>
                          </Box>
                          <Chip label="Auto-Approve" variant="outlined" size="small" />
                          <Box display="flex" gap={1} sx={{ ml: 'auto' }}>
                            <IconButton
                              size="small"
                              onClick={() => handleEditRule(rule)}
                            >
                              <Edit />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteRule(rule.id)}
                            >
                              <Delete />
                            </IconButton>
                          </Box>
                        </>
                      )}
                    </Box>
                  </ListItem>
                ))}
              </List>

              {autoApprovalRules.length === 0 && (
                <Box textAlign="center" py={4}>
                  <Settings color="primary" sx={{ fontSize: 60, mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    No Auto-Approval Rules
                  </Typography>
                  <Typography color="textSecondary">
                    Create rules to automatically approve transactions based on keywords.
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Paper>

      {/* Edit Transaction Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCancelEditTransaction} maxWidth="md" fullWidth>
        <DialogTitle>Edit Transaction</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Amount"
                  type="number"
                  value={editFormData.amount}
                  onChange={(e) => setEditFormData({ ...editFormData, amount: e.target.value })}
                  InputProps={{
                    startAdornment: <Typography variant="body2" sx={{ mr: 1 }}>₹</Typography>,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Description"
                  value={editFormData.description}
                  onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Date & Time"
                  type="datetime-local"
                  value={editFormData.date}
                  onChange={(e) => setEditFormData({ ...editFormData, date: e.target.value })}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Transaction Type</InputLabel>
                  <Select
                    value={editFormData.transaction_type}
                    onChange={(e) => setEditFormData({ ...editFormData, transaction_type: e.target.value as any })}
                    label="Transaction Type"
                  >
                    <MenuItem value="income">Income</MenuItem>
                    <MenuItem value="expense">Expense</MenuItem>
                    <MenuItem value="transfer">Transfer</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={editFormData.category_id}
                    onChange={(e) => setEditFormData({ ...editFormData, category_id: e.target.value })}
                    label="Category"
                  >
                    {categories.map((category) => (
                      <MenuItem key={category.id} value={category.id.toString()}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              backgroundColor: category.color
                            }}
                          />
                          <span>{category.name}</span>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Bank</InputLabel>
                  <Select
                    value={editFormData.bank_id}
                    onChange={(e) => setEditFormData({ ...editFormData, bank_id: e.target.value })}
                    label="Bank"
                  >
                    {banks.map((bank) => (
                      <MenuItem key={bank.id} value={bank.id.toString()}>
                        {bank.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Reference Number (Optional)"
                  value={editFormData.reference_number}
                  onChange={(e) => setEditFormData({ ...editFormData, reference_number: e.target.value })}
                />
              </Grid>
              {(editFormData.transaction_type === 'transfer' || (editingTransaction as any)?.to_bank || (editingTransaction as any)?.to_bank_name || editFormData.to_bank_id || editFormData.to_bank_name) && (
                <>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>To Bank</InputLabel>
                      <Select
                        value={editFormData.to_bank_id}
                        onChange={(e) => setEditFormData({ ...editFormData, to_bank_id: e.target.value })}
                        label="To Bank"
                      >
                        <MenuItem value="">Select Bank</MenuItem>
                        {banks.map((bank) => (
                          <MenuItem key={bank.id} value={bank.id.toString()}>
                            {bank.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="To Bank Name (Optional)"
                      value={editFormData.to_bank_name}
                      onChange={(e) => setEditFormData({ ...editFormData, to_bank_name: e.target.value })}
                      placeholder="External bank or service name"
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEditTransaction}>Cancel</Button>
          <Button onClick={handleSaveTransaction} variant="contained" color="primary">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TransactionApprovalsPage;
